/**
 * Bulk Sale workspace — one customer, N vehicles, one bulk payment.
 *
 * Pricing model:
 *   • Each vehicle bills at its OWN on-road price derived from its OWN
 *     inventory-attached package(s).
 *   • Bulk payment is one lump sum; any shortfall (grand_on_road − paid)
 *     is booked as a WRITE-OFF distributed EQUALLY across N vehicles
 *     (last row absorbs paise). Requires a reason.
 *   • Payment is allocated pro-rata by per-vehicle net (on-road − writeoff_share).
 *   • Optional add-ons are INCLUDED by default; each selected VIN shows
 *     its optional items as inline click-to-toggle chips so operators can
 *     drop an add-on for that VIN only.
 *
 * Each VIN becomes its own Sale (own invoice, own receipt) so RTO
 * registration can be issued per vehicle.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import BranchSelect from "@/components/BranchSelect";
import { SaleCustomerSelect } from "@/components/SaleSelects";
import { fmtMoney } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Truck, Printer, MagnifyingGlass, X, Check, Plus } from "@phosphor-icons/react";
import { printPaymentReceiptBatch } from "@/lib/paymentReceipt";
import CustomerPinGate from "@/components/CustomerPinGate";
import { customerPin } from "@/lib/india";

const PAYMENT_METHODS = [
  { v: "cash", l: "Cash" },
  { v: "upi", l: "UPI" },
  { v: "online", l: "Online / NEFT" },
  { v: "card", l: "Card" },
];

const GST_TYPES = [
  { v: "intra", l: "Intra-state (CGST + IGST)" },
  { v: "inter", l: "Inter-state (IGST only)" },
];

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Mirror backend `_snapshot_packages` on-road computation for previews. */
const computeVehicleOnRoad = (vehicle, pkgById, gstType, excludedByPkg) => {
  const pkgIds = vehicle.package_ids || [];
  if (pkgIds.length === 0) return 0;
  let total = 0;
  for (const pid of pkgIds) {
    const pkg = pkgById[pid];
    if (!pkg) continue;
    const items = pkg.items || [];
    const exSet = new Set(excludedByPkg?.[pid] || []);
    let sub = 0;
    let cgstToMerge = 0;
    if (gstType === "inter") {
      const cgstRow = items.find((it) => it.label === "CGST");
      if (cgstRow) cgstToMerge = Number(cgstRow.price) || 0;
    }
    for (const it of items) {
      if (gstType === "inter" && it.label === "CGST") continue;
      if (it.optional && exSet.has(it.label)) continue;
      let price = Number(it.price) || 0;
      if (gstType === "inter" && it.label === "IGST" && cgstToMerge > 0) {
        price += cgstToMerge;
      }
      sub += price;
    }
    if (sub === 0) sub = Number(pkg.price) || 0;
    total += sub;
  }
  return round2(total);
};

export default function BulkSalePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [branchId, setBranchId] = useState(user?.branch_id || "");
  const [customerId, setCustomerId] = useState("");
  const [salesAgentId, setSalesAgentId] = useState("");
  const [gstType, setGstType] = useState("intra");
  const [modelCategory, setModelCategory] = useState("");
  const [writeoffReason, setWriteoffReason] = useState("");
  const [notes, setNotes] = useState("");

  const [customers, setCustomers] = useState([]);
  const [pinFixed, setPinFixed] = useState({});
  const [agents, setAgents] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [packages, setPackages] = useState([]);
  const [searchQ, setSearchQ] = useState("");

  // Selected vehicle ids (set semantics)
  const [selected, setSelected] = useState({}); // {vehicle_id: true}

  // Per-vehicle item exclusions.
  // Shape: { [vehicle_id]: { [package_id]: [item_label, ...] } }
  const [exclusions, setExclusions] = useState({});

  const [payment, setPayment] = useState({
    method: "cash",
    amount: "",
    reference: "",
    paid_on: new Date().toISOString().slice(0, 10),
  });

  const [submitting, setSubmitting] = useState(false);

  // Bootstrap customers
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const custRes = await api.get("/customers");
        if (cancelled) return;
        setCustomers(custRes.data || []);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reload agents + vehicles + packages when branch changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!branchId) {
        setAgents([]);
        setVehicles([]);
        setPackages([]);
        return;
      }
      try {
        const [agentsRes, vehRes, pkgRes] = await Promise.all([
          api.get("/sales-agents", { params: { active_only: true, branch_id: branchId } }),
          api.get("/vehicles", { params: { branch_id: branchId, status: "available" } }),
          api.get("/packages", { params: { active_only: true, branch_id: branchId } }),
        ]);
        if (cancelled) return;
        setAgents(agentsRes.data || []);
        setVehicles((vehRes.data || []).filter((v) => v.status === "available"));
        setPackages(pkgRes.data || []);
        const stillValid = (agentsRes.data || []).some((a) => a.id === salesAgentId);
        if (salesAgentId && !stillValid) setSalesAgentId("");
        const validIds = new Set((vehRes.data || []).map((v) => v.id));
        setSelected((prev) => {
          const next = {};
          Object.keys(prev).forEach((id) => {
            if (validIds.has(id)) next[id] = prev[id];
          });
          return next;
        });
        setExclusions((prev) => {
          const next = {};
          for (const k of Object.keys(prev)) if (validIds.has(k)) next[k] = prev[k];
          return next;
        });
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId]);

  const pkgById = useMemo(() => {
    const m = {};
    for (const p of packages) m[p.id] = p;
    return m;
  }, [packages]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    for (const v of vehicles) {
      for (const pid of v.package_ids || []) {
        const pkg = pkgById[pid];
        if (pkg?.model_category) set.add(pkg.model_category.toUpperCase());
      }
    }
    return Array.from(set).sort();
  }, [vehicles, pkgById]);

  const filteredVehicles = useMemo(() => {
    let pool = vehicles;
    if (modelCategory) {
      pool = pool.filter((v) =>
        (v.package_ids || []).some(
          (pid) => (pkgById[pid]?.model_category || "").toUpperCase() === modelCategory,
        ),
      );
    }
    const needle = searchQ.trim().toLowerCase();
    if (!needle) return pool;
    return pool.filter((v) => {
      const hay = [v.vin, v.package_name, v.color, v.variant].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [vehicles, modelCategory, searchQ, pkgById]);

  const selectedRows = useMemo(() => {
    const out = [];
    for (const v of vehicles) {
      if (selected[v.id]) {
        const excl = exclusions[v.id] || {};
        const on_road = computeVehicleOnRoad(v, pkgById, gstType, excl);
        out.push({ vehicle: v, on_road, exclusions: excl });
      }
    }
    return out;
  }, [vehicles, selected, pkgById, gstType, exclusions]);

  const n = selectedRows.length;
  const cumulativeOnRoad = round2(selectedRows.reduce((s, r) => s + r.on_road, 0));
  const paymentAmount = Math.max(0, Number(payment.amount || 0));
  const writeoffTotal = Math.max(0, round2(cumulativeOnRoad - paymentAmount));
  const cumulativeNet = round2(cumulativeOnRoad - writeoffTotal);

  const allocations = useMemo(() => {
    if (n === 0) return [];
    const woShares = [];
    let woRunning = 0;
    for (let i = 0; i < n; i++) {
      let wo;
      if (writeoffTotal <= 0) wo = 0;
      else if (i === n - 1) wo = round2(writeoffTotal - woRunning);
      else {
        wo = round2(writeoffTotal / n);
        woRunning += wo;
      }
      woShares.push(Math.max(0, wo));
    }
    const netShares = selectedRows.map((r, i) => Math.max(0, round2(r.on_road - woShares[i])));
    const totalNet = round2(netShares.reduce((s, v) => s + v, 0));
    const payShares = [];
    let pRunning = 0;
    for (let i = 0; i < n; i++) {
      let a;
      if (paymentAmount <= 0 || totalNet <= 0) a = 0;
      else if (i === n - 1) a = round2(paymentAmount - pRunning);
      else {
        a = round2((paymentAmount * netShares[i]) / totalNet);
        pRunning += a;
      }
      payShares.push(Math.max(0, a));
    }
    return selectedRows.map((_, i) => ({ writeoff: woShares[i], net: netShares[i], payment: payShares[i] }));
  }, [selectedRows, n, writeoffTotal, paymentAmount]);

  const toggleVehicle = (id) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
    setExclusions((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const removeRow = (id) => {
    setSelected((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setExclusions((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const toggleItemExclusion = (vehicleId, packageId, label) => {
    setExclusions((prev) => {
      const forVeh = { ...(prev[vehicleId] || {}) };
      const list = new Set(forVeh[packageId] || []);
      if (list.has(label)) list.delete(label);
      else list.add(label);
      if (list.size === 0) delete forVeh[packageId];
      else forVeh[packageId] = Array.from(list);
      const next = { ...prev };
      if (Object.keys(forVeh).length === 0) delete next[vehicleId];
      else next[vehicleId] = forVeh;
      return next;
    });
  };

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const pinMissing = !!selectedCustomer && !customerPin(selectedCustomer) && !pinFixed[customerId];

  const validate = () => {
    if (!branchId) return "Pick a branch";
    if (!customerId) return "Pick a customer";
    if (pinMissing) return "Customer PIN code is required before creating the sale invoice";
    if (!salesAgentId) return "Pick a sales agent";
    if (n === 0) return "Select at least one vehicle";
    if (cumulativeOnRoad <= 0)
      return "One or more selected vehicles have no attached package — attach a package in Inventory first";
    if (paymentAmount > cumulativeOnRoad + 0.01) {
      return `Payment ₹${fmtMoney(paymentAmount)} exceeds cumulative on-road ₹${fmtMoney(cumulativeOnRoad)}`;
    }
    if (writeoffTotal > 0.01 && !writeoffReason.trim()) {
      return `Write-off ₹${fmtMoney(writeoffTotal)} requires a reason`;
    }
    if (payment.method !== "cash" && paymentAmount > 0 && !payment.reference.trim()) {
      return `${payment.method.toUpperCase()} payment requires a reference (UTR / txn id)`;
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        customer_id: customerId,
        branch_id: branchId,
        sales_agent_id: salesAgentId,
        gst_type: gstType,
        vehicles: selectedRows.map((r) => {
          const excl = r.exclusions || {};
          const line = { vehicle_id: r.vehicle.id };
          if (Object.keys(excl).length > 0) line.package_item_exclusions = excl;
          return line;
        }),
        payment: {
          method: payment.method,
          amount: paymentAmount,
          reference: payment.reference.trim(),
          paid_on: payment.paid_on,
        },
        writeoff_reason: writeoffTotal > 0 ? writeoffReason.trim() : "",
        notes,
      };
      const { data } = await api.post("/sales/bulk", body);
      toast.success(`Created ${data.vehicle_count} sales · group ${data.bulk_sale_group_id.slice(0, 8)}`);

      try {
        const dealershipRes = await api.get("/settings/dealership");
        const dealership = dealershipRes.data || {};
        const payloads = (data.sales || [])
          .filter((s) => (s.payment_lines || []).length > 0 && (s.payment_lines[0].amount || 0) > 0)
          .map((s) => ({
            dealership,
            receipt_no: s.payment_lines[0].receipt_no || "—",
            now: new Date().toISOString(),
            line: s.payment_lines[0],
            customer: s.customer || {},
            vehicle: s.vehicle || {},
            sale: {
              invoice_no: s.id?.slice(0, 8).toUpperCase(),
              on_road_price: s.on_road_price,
            },
            outstanding_after: Number(s.due) || 0,
            received_by: user?.name || "",
          }));
        if (payloads.length > 0) printPaymentReceiptBatch(payloads, { autoPrint: false });
      } catch {
        /* receipt print is a convenience */
      }
      navigate("/sales");
    } catch (err2) {
      toast.error(formatApiError(err2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="02 / Sales · Bulk"
        title="Bulk Sale"
        subtitle="One customer buys multiple vehicles, paid in one transaction. Each vehicle bills at its OWN inventory price. Any shortfall becomes an equally-split write-off. Every vehicle still gets its own invoice & receipt for individual RTO registration."
        actions={
          <button
            onClick={() => navigate("/sales")}
            data-testid="bulk-sale-back"
            className="px-3 py-2 border border-canvas-line text-ink text-xs font-semibold hover:border-ink flex items-center gap-1.5"
          >
            <ArrowLeft size={12} weight="bold" />
            Back to Sales
          </button>
        }
      />

      <div className="px-8 py-6 space-y-6">
        {/* Header config row */}
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 border border-canvas-line p-4">
          <BranchSelect value={branchId} onChange={(v) => setBranchId(v)} activeOnly />
          <div>
            <SaleCustomerSelect customers={customers} value={customerId} onChange={setCustomerId} />
            {pinMissing && (
              <div className="mt-2">
                <CustomerPinGate
                  key={customerId}
                  customerId={customerId}
                  onSaved={() => setPinFixed((m) => ({ ...m, [customerId]: true }))}
                />
              </div>
            )}
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">Sales Agent</label>
            <select
              value={salesAgentId}
              onChange={(e) => setSalesAgentId(e.target.value)}
              data-testid="bulk-sale-agent"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="">Select agent…</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">GST Type</label>
            <select
              value={gstType}
              onChange={(e) => setGstType(e.target.value)}
              data-testid="bulk-sale-gst"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              {GST_TYPES.map((g) => (
                <option key={g.v} value={g.v}>{g.l}</option>
              ))}
            </select>
          </div>
        </section>

        {/* Two-column main: left = vehicle picker, right = selected + payment */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="border border-canvas-line bg-canvas-card" data-testid="bulk-sale-vehicle-picker">
            <div className="px-4 py-3 border-b border-canvas-line flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Truck size={14} weight="bold" />
                <h3 className="font-display text-sm font-bold">Available stock</h3>
                <span className="font-mono text-[10px] text-ink-subtle">({filteredVehicles.length})</span>
              </div>
              <div className="flex items-center gap-2 flex-1 max-w-md justify-end">
                <select
                  value={modelCategory}
                  onChange={(e) => setModelCategory(e.target.value)}
                  data-testid="bulk-sale-model-category"
                  className="px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
                >
                  <option value="">All categories</option>
                  {categoryOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <div className="relative flex-1 max-w-xs">
                  <MagnifyingGlass size={12} weight="bold" className="absolute left-2 top-2.5 text-ink-subtle" />
                  <input
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="VIN / color…"
                    data-testid="bulk-sale-vehicle-search"
                    className="w-full pl-7 pr-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
                  />
                </div>
              </div>
            </div>
            <div className="max-h-[560px] overflow-y-auto">
              {!branchId ? (
                <div className="p-8 text-center text-xs text-ink-subtle font-mono">Pick a branch to load stock.</div>
              ) : filteredVehicles.length === 0 ? (
                <div className="p-8 text-center text-xs text-ink-subtle font-mono">No available VINs in this branch{searchQ || modelCategory ? " match the filters" : ""}.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono text-[9px] uppercase tracking-wider text-ink-muted w-8"></th>
                      <th className="text-left px-2 py-2 font-mono text-[9px] uppercase tracking-wider text-ink-muted">VIN</th>
                      <th className="text-left px-2 py-2 font-mono text-[9px] uppercase tracking-wider text-ink-muted">Package</th>
                      <th className="text-right px-2 py-2 font-mono text-[9px] uppercase tracking-wider text-ink-muted">On-Road</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVehicles.map((v) => {
                      const isSel = !!selected[v.id];
                      const on_road = computeVehicleOnRoad(v, pkgById, gstType, {});
                      return (
                        <tr
                          key={v.id}
                          onClick={() => toggleVehicle(v.id)}
                          data-testid={`bulk-vehicle-row-${v.id}`}
                          className={`border-b border-canvas-line cursor-pointer ${
                            isSel ? "bg-ink/5" : "hover:bg-canvas-panel"
                          }`}
                        >
                          <td className="px-3 py-1.5">
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={() => toggleVehicle(v.id)}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`bulk-vehicle-check-${v.id}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[10px]">
                            {v.vin}
                            <div className="text-ink-subtle text-[9px]">{v.color || "—"}</div>
                          </td>
                          <td className="px-2 py-1.5">
                            <div>{v.package_name || v.model_name || "—"}</div>
                            {(v.package_ids || []).length === 0 && (
                              <div className="text-destructive text-[9px] font-mono">No package attached</div>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{on_road > 0 ? fmtMoney(on_road) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className="space-y-4">
            <div className="border border-canvas-line bg-canvas-card">
              <div className="px-4 py-3 border-b border-canvas-line">
                <h3 className="font-display text-sm font-bold">Selected vehicles ({n})</h3>
                <div className="font-mono text-[10px] text-ink-subtle mt-0.5">
                  Add-on items are shown as chips under each VIN — click to include or exclude for that VIN only.
                </div>
              </div>
              {n === 0 ? (
                <div className="p-8 text-center text-xs text-ink-subtle font-mono">Pick at least one vehicle from the left.</div>
              ) : (
                <div className="max-h-[420px] overflow-y-auto divide-y divide-canvas-line">
                  {selectedRows.map((r, idx) => {
                    const a = allocations[idx] || { writeoff: 0, net: 0, payment: 0 };
                    const vehPkgs = (r.vehicle.package_ids || [])
                      .map((pid) => pkgById[pid])
                      .filter(Boolean);
                    // Collect optional items as chips across all packages on this VIN
                    const chips = [];
                    for (const pkg of vehPkgs) {
                      for (const it of pkg.items || []) {
                        if (gstType === "inter" && it.label === "CGST") continue;
                        if (!it.optional) continue;
                        const excluded = new Set((r.exclusions || {})[pkg.id] || []);
                        chips.push({
                          pkgId: pkg.id,
                          label: it.label,
                          price: Number(it.price) || 0,
                          included: !excluded.has(it.label),
                        });
                      }
                    }
                    return (
                      <div
                        key={r.vehicle.id}
                        data-testid={`bulk-selected-row-${r.vehicle.id}`}
                        className="px-3 py-2.5"
                      >
                        {/* Top line: VIN · package · price · remove */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-mono text-[11px] text-ink font-semibold">{r.vehicle.vin}</div>
                            <div className="text-[10px] text-ink-subtle">
                              {r.vehicle.package_name || r.vehicle.model_name || "—"}
                              {r.vehicle.color ? ` · ${r.vehicle.color}` : ""}
                            </div>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <div className="font-mono tabular-nums text-sm font-bold" data-testid={`bulk-onroad-${r.vehicle.id}`}>
                              ₹{fmtMoney(r.on_road)}
                            </div>
                            {a.writeoff > 0 && (
                              <div className="font-mono tabular-nums text-[10px] text-destructive">
                                −₹{fmtMoney(a.writeoff)} write-off
                              </div>
                            )}
                            {a.payment > 0 && (
                              <div className="font-mono tabular-nums text-[10px] text-[#00713a]">
                                ₹{fmtMoney(a.payment)} paid
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeRow(r.vehicle.id)}
                            title="Remove"
                            data-testid={`bulk-remove-${r.vehicle.id}`}
                            className="p-1 border border-canvas-line hover:border-destructive hover:text-destructive"
                          >
                            <X size={10} weight="bold" />
                          </button>
                        </div>

                        {/* Add-on chips row — always visible */}
                        {chips.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1" data-testid={`bulk-chips-${r.vehicle.id}`}>
                            {chips.map((c) => (
                              <button
                                key={`${c.pkgId}-${c.label}`}
                                type="button"
                                onClick={() => toggleItemExclusion(r.vehicle.id, c.pkgId, c.label)}
                                data-testid={`bulk-item-${r.vehicle.id}-${c.pkgId}-${c.label}`}
                                title={c.included ? "Click to exclude for this VIN" : "Click to include for this VIN"}
                                className={`px-2 py-0.5 border text-[10px] font-mono flex items-center gap-1 transition-colors ${
                                  c.included
                                    ? "border-ink bg-ink text-white hover:bg-brand hover:border-brand"
                                    : "border-canvas-line bg-canvas text-ink-subtle hover:border-ink hover:text-ink"
                                }`}
                              >
                                {c.included
                                  ? <Check size={8} weight="bold" />
                                  : <Plus size={8} weight="bold" />}
                                <span>{c.label}</span>
                                <span className="tabular-nums opacity-80">₹{fmtMoney(c.price)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Totals footer */}
                  <div className="bg-canvas-panel px-3 py-2 flex items-center justify-between text-xs">
                    <span className="font-mono text-[10px] font-bold uppercase">Total on-road</span>
                    <span className="font-mono tabular-nums font-bold" data-testid="bulk-cumulative-onroad">
                      ₹{fmtMoney(cumulativeOnRoad)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="border border-canvas-line bg-canvas-card p-4 space-y-3" data-testid="bulk-sale-payment">
              <h3 className="font-display text-sm font-bold">Bulk payment</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">Method</label>
                  <select
                    value={payment.method}
                    onChange={(e) => setPayment({ ...payment, method: e.target.value })}
                    data-testid="bulk-payment-method"
                    className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.v} value={m.v}>{m.l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={payment.amount}
                    onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
                    placeholder="0.00"
                    data-testid="bulk-payment-amount"
                    className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">
                    Reference {payment.method !== "cash" && <span className="text-destructive">*</span>}
                  </label>
                  <input
                    value={payment.reference}
                    onChange={(e) => setPayment({ ...payment, reference: e.target.value })}
                    placeholder={payment.method === "cash" ? "Optional" : "UTR / txn id"}
                    data-testid="bulk-payment-reference"
                    className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">Paid on</label>
                  <input
                    type="date"
                    value={payment.paid_on}
                    onChange={(e) => setPayment({ ...payment, paid_on: e.target.value })}
                    data-testid="bulk-payment-paid-on"
                    className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
                  />
                </div>
              </div>
              {writeoffTotal > 0 && (
                <div className="pt-2 border-t border-canvas-line">
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">
                    Write-off reason <span className="text-destructive">*</span>
                    <span className="ml-2 normal-case text-ink-subtle">
                      ₹{fmtMoney(writeoffTotal)} · split equally across {n} vehicles (≈ ₹{fmtMoney(round2(writeoffTotal / n))} each)
                    </span>
                  </label>
                  <input
                    value={writeoffReason}
                    onChange={(e) => setWriteoffReason(e.target.value)}
                    placeholder="e.g. fleet negotiation, bad debt settlement"
                    data-testid="bulk-writeoff-reason"
                    className="w-full px-3 py-2 bg-canvas border border-destructive/50 text-sm focus:outline-none focus:border-destructive"
                  />
                </div>
              )}
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="border border-canvas-line p-2">
                  <div className="font-mono text-[9px] uppercase text-ink-muted">On-Road</div>
                  <div className="font-mono tabular-nums font-bold mt-0.5">{fmtMoney(cumulativeOnRoad)}</div>
                </div>
                <div className={`border p-2 ${writeoffTotal > 0 ? "border-destructive bg-destructive/5" : "border-canvas-line"}`}>
                  <div className="font-mono text-[9px] uppercase text-ink-muted">− Write-Off</div>
                  <div className={`font-mono tabular-nums font-bold mt-0.5 ${writeoffTotal > 0 ? "text-destructive" : ""}`} data-testid="bulk-writeoff-total">
                    {fmtMoney(writeoffTotal)}
                  </div>
                </div>
                <div className="border border-[#00713a] bg-[#00B341]/5 p-2">
                  <div className="font-mono text-[9px] uppercase text-ink-muted">= Net</div>
                  <div className="font-mono tabular-nums font-bold text-[#00713a] mt-0.5" data-testid="bulk-cumulative-net">{fmtMoney(cumulativeNet)}</div>
                </div>
                <div className="border border-canvas-line p-2">
                  <div className="font-mono text-[9px] uppercase text-ink-muted">Paid</div>
                  <div className="font-mono tabular-nums font-bold mt-0.5">{fmtMoney(paymentAmount)}</div>
                </div>
              </div>
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">Notes (audit trail)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional context — e.g. ABC Logistics fleet purchase"
                data-testid="bulk-notes"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => navigate("/sales")}
                className="px-4 py-2 border border-canvas-line text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || n === 0}
                data-testid="bulk-submit"
                className="px-5 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60 flex items-center gap-1.5"
              >
                <Printer size={12} weight="bold" />
                {submitting ? "Creating…" : `Create ${n || ""} sales & print`}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
